import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import logoWhite from '../../images/logo-white.png';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://www.ridenrepair.com/api';
const SHIELD = '/rnr-shield.png';

const C = {
  bg: '#0b0d10', card: '#15181d', border: 'rgba(255,255,255,.09)',
  red: '#f42222', redDeep: '#8b0f0f', danger: '#d93025', green: '#1bbf73', amber: '#f59e0b',
  white: '#ffffff', muted: 'rgba(255,255,255,.66)', faint: 'rgba(255,255,255,.42)',
};
const FONT = "'Poppins', system-ui, -apple-system, sans-serif";

class QrVerifyPage extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, data: null, err: false, activating: false, activated: null, activateErr: false, open: {} };
  }
  getCode(p = this.props) {
    return String(p.code || (p.match && p.match.params && p.match.params.code) || '').trim();
  }
  componentDidMount() { this.load(); }
  componentDidUpdate(prev) { if (this.getCode(prev) !== this.getCode()) this.load(); }

  load = async () => {
    this.setState({ loading: true, err: false, activated: null });
    try {
      const r = await fetch(`${API_BASE}/spares/unit/${encodeURIComponent(this.getCode())}/verify`);
      this.setState({ loading: false, data: await r.json() });
    } catch (e) { this.setState({ loading: false, err: true }); }
  };

  activate = async () => {
    this.setState({ activating: true, activateErr: false });
    try {
      const r = await fetch(`${API_BASE}/spares/unit/${encodeURIComponent(this.getCode())}/activate`, { method: 'POST' });
      const res = await r.json();
      if (res && res.ok) this.setState({ activating: false, activated: res });
      else this.setState({ activating: false, activateErr: true });
    } catch (e) { this.setState({ activating: false, activateErr: true }); }
  };

  render() {
    const { loading, data, err, activated } = this.state;
    const code = this.getCode();
    const genuine = data && data.genuine;
    return (
      <>
        <Helmet>
          <title>Verify Genuine Product · Ride N Repair</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Helmet>
        <style>{`
          @keyframes rnrvPulse{0%,100%{opacity:.5;transform:scale(.96)}50%{opacity:1;transform:scale(1)}}
          @keyframes rnrvUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
          @keyframes rnrvPop{0%{transform:scale(.5);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}
          .rnrv-up{animation:rnrvUp .5s cubic-bezier(.2,.7,.2,1) both}
          .rnrv-pop{animation:rnrvPop .5s cubic-bezier(.2,.8,.2,1) both}
          .rnrv-cta{transition:filter .15s, transform .1s}
          .rnrv-cta:hover{filter:brightness(1.1)} .rnrv-cta:active{transform:translateY(1px)}
        `}</style>
        <div style={{ minHeight: '100vh', background: `radial-gradient(130% 90% at 50% -15%, #1b1f27 0%, ${C.bg} 58%)`,
          color: C.white, fontFamily: FONT, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '26px 18px 46px' }}>
          <img src={logoWhite} alt="Ride N Repair" style={{ height: 30, marginBottom: 26 }} />
          <div style={{ width: '100%', maxWidth: 440 }}>
            {activated ? this.renderActivated(activated, data)
              : loading ? this.renderLoading()
              : (err || !data) ? this.renderError()
              : genuine ? this.renderGenuine(data)
              : this.renderNotGenuine(data)}
          </div>
          <div style={{ marginTop: 'auto', paddingTop: 32, color: C.faint, fontSize: 11.5, textAlign: 'center', letterSpacing: '.03em' }}>
            RIDE N REPAIR · GENUINE PARTS PROGRAM
            {code ? <div style={{ marginTop: 4 }}>ID <span style={{ fontFamily: 'monospace', color: C.muted }}>{code}</span></div> : null}
          </div>
        </div>
      </>
    );
  }

  txt(s, style) { return <div style={{ color: C.white, ...style }}>{s}</div>; }

  // Product-agnostic copy helpers. Legacy (Ironclad) responses carry neither field,
  // so the defaults — 'bottle' + sealed — keep that page reading exactly as before.
  noun(p) {
    const n = String((p && p.unit_noun) || 'bottle').toLowerCase();
    return ['bottle', 'part', 'pack', 'set'].indexOf(n) !== -1 ? n : 'part';
  }
  hasSeal(p) { return !(p && p.has_tamper_seal === false); }
  warrantyDays(d) {
    const n = Number(d && d.warranty_days);
    return (d && d.warranty_days != null && Number.isFinite(n) && n >= 0) ? Math.round(n) : 30;
  }
  fmtMonth(s) {
    const m = /^(\d{4})-(\d{2})/.exec(String(s || ''));
    return m ? `${m[2]}/${m[1]}` : (s ? String(s) : null);
  }

  metaRow(d) {
    if (!d) return null;
    const p = d.product || {};
    const cells = [];
    if (d.sku) cells.push(['SKU', d.sku]);
    if (d.batch_no) cells.push(['Batch No.', d.batch_no]);
    if (p.net_quantity) cells.push(['Net qty', p.net_quantity]);
    if (d.manufactured_on) cells.push(['Mfg', this.fmtMonth(d.manufactured_on)]);
    if (d.best_before) cells.push(['Best before', this.fmtMonth(d.best_before)]);
    if (!cells.length) return null;
    const wrap = cells.length > 2;
    const cell = (label, val) => (
      <div key={label} style={wrap ? { flex: '1 1 30%', minWidth: 92, textAlign: 'center' } : { flex: 1, textAlign: 'center' }}>
        <div style={{ fontSize: 9.5, letterSpacing: '.14em', color: C.faint, textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.white, fontFamily: 'monospace', marginTop: 2, wordBreak: 'break-word' }}>{val}</div>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexWrap: wrap ? 'wrap' : 'nowrap', gap: 10, rowGap: 12, margin: '16px 0 0', padding: '11px 8px',
        background: 'rgba(255,255,255,.04)', border: `1px solid ${C.border}`, borderRadius: 12 }}>
        {wrap
          ? cells.map(c => cell(c[0], c[1]))
          : cells.reduce((acc, c, i) => acc.concat(
              i ? [<div key={`sep${i}`} style={{ width: 1, background: C.border }} />] : [], [cell(c[0], c[1])]), [])}
      </div>
    );
  }

  chips(list) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6, margin: '10px 0 0' }}>
        {list.map((s, i) => (
          <span key={i} style={{ fontSize: 11.5, color: 'rgba(255,255,255,.86)', padding: '5px 10px', borderRadius: 999,
            background: 'rgba(255,255,255,.05)', border: `1px solid ${C.border}`, lineHeight: 1.2 }}>{s}</span>
        ))}
      </div>
    );
  }

  // Closed-by-default disclosure rows (44px tap target). Text is rendered as plain React text.
  collapsible(key, label, body) {
    if (!body || !String(body).trim()) return null;
    const open = !!this.state.open[key];
    return (
      <div key={key} style={{ borderTop: `1px solid ${C.border}` }}>
        <button type="button" aria-expanded={open}
          onClick={() => this.setState(s => ({ open: { ...s.open, [key]: !s.open[key] } }))}
          style={{ width: '100%', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            background: 'transparent', border: 'none', padding: '0 2px', cursor: 'pointer', color: C.white,
            fontFamily: FONT, fontSize: 13.5, fontWeight: 600, textAlign: 'left' }}>
          <span style={{ color: C.white }}>{label}</span>
          <span style={{ color: C.faint, fontSize: 12, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▸</span>
        </button>
        {open ? (
          <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, textAlign: 'left', whiteSpace: 'pre-line', padding: '0 2px 14px' }}>{String(body).trim()}</div>
        ) : null}
      </div>
    );
  }

  details(p) {
    const rows = [
      this.collapsible('about', 'About this product', p.description),
      this.collapsible('usage', 'How to use & cautions', p.usage_notes),
      this.collapsible('terms', 'Warranty terms', p.warranty_terms),
    ].filter(Boolean);
    if (!rows.length) return null;
    return <div style={{ margin: '18px 0 0', borderBottom: `1px solid ${C.border}` }}>{rows}</div>;
  }

  footMeta(p) {
    const line = [p.marketed_by ? `Marketed by ${p.marketed_by}` : null, p.country_of_origin ? `Made in ${p.country_of_origin}` : null]
      .filter(Boolean).join(' · ');
    if (!line && !p.care_contact) return null;
    return (
      <div style={{ margin: '16px 0 0' }}>
        {line ? this.txt(line, { fontSize: 11.5, color: C.faint, lineHeight: 1.6 }) : null}
        {p.care_contact ? this.txt(<>Customer care · <span style={{ color: C.muted }}>{p.care_contact}</span></>, { fontSize: 11.5, color: C.faint, lineHeight: 1.6 }) : null}
      </div>
    );
  }

  renderLoading() {
    return (
      <div style={{ textAlign: 'center', padding: '52px 0' }}>
        <img src={SHIELD} alt="" style={{ width: 88, animation: 'rnrvPulse 1.2s ease-in-out infinite' }} />
        <div style={{ marginTop: 22, color: C.muted, letterSpacing: '.05em', fontSize: 14 }}>Verifying authenticity…</div>
      </div>
    );
  }

  badge(color, glyph, pop) {
    return (
      <div className={pop ? 'rnrv-pop' : ''} style={{ width: 78, height: 78, borderRadius: '50%', margin: '0 auto 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}1f`, border: `2px solid ${color}`, boxShadow: `0 0 40px ${color}55` }}>
        <span style={{ fontSize: 38, lineHeight: 1, color, fontWeight: 700 }}>{glyph}</span>
      </div>
    );
  }
  card(children, accent) {
    return (
      <div className="rnrv-up" style={{ background: C.card, border: `1px solid ${C.border}`, borderTop: `3px solid ${accent}`,
        borderRadius: 20, padding: '30px 22px', boxShadow: '0 26px 64px rgba(0,0,0,.55)' }}>{children}</div>
    );
  }
  btn(color, solid) {
    return { flex: 1, textAlign: 'center', padding: '13px 12px', borderRadius: 12, fontWeight: 600, fontSize: 14,
      textDecoration: 'none', color: C.white, cursor: 'pointer', border: solid ? 'none' : `1px solid ${C.border}`,
      background: solid ? `linear-gradient(180deg, ${C.red}, ${C.redDeep})` : 'transparent' };
  }

  renderGenuine(d) {
    const p = d.product || {};
    const noun = this.noun(p);
    const wd = this.warrantyDays(d);
    const ctaLabel = wd > 0 ? `Activate warranty · ${wd} days` : 'Register this product';
    const chipList = Array.isArray(p.spec_chips) ? p.spec_chips.filter(Boolean) : [];
    const expired = !!(d.warranty && Number(d.warranty.days_remaining) <= 0);
    return this.card(
      <div style={{ textAlign: 'center' }}>
        {this.badge(C.green, '✓')}
        {this.txt('GENUINE', { fontSize: 12.5, letterSpacing: '.2em', color: C.green, fontWeight: 700 })}
        {this.txt(p.name || 'Genuine Product', { fontSize: 25, fontWeight: 700, margin: '6px 0 2px' })}
        {this.txt('Verified by Ride N Repair', { fontSize: 13, color: C.muted })}

        {(p.images && p.images.length > 1) ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 16, margin: '18px 0 10px' }}>
            {p.images.slice(0, 4).map((src, i) => <img key={i} src={src} alt={p.name} style={{ height: 190, maxWidth: `${Math.floor(100 / Math.min(p.images.length, 4)) - 4}%`, objectFit: 'contain', filter: 'drop-shadow(0 16px 32px rgba(0,0,0,.6))' }} />)}
          </div>
        ) : (p.image || (p.images && p.images[0])) ? (
          <img src={p.image || p.images[0]} alt={p.name} style={{ height: 176, maxWidth: '82%', objectFit: 'contain', margin: '18px auto 10px', display: 'block', filter: 'drop-shadow(0 14px 30px rgba(0,0,0,.6))' }} />
        ) : null}

        {p.line ? this.txt(p.line, { fontSize: 15, fontWeight: 600 }) : null}
        {chipList.length ? this.chips(chipList)
          : p.specs ? this.txt(p.specs, { fontSize: 12.5, color: C.muted, margin: '6px 0 0' }) : null}
        {p.fitment ? this.txt(<><span style={{ color: C.faint }}>Fits</span> · <span style={{ color: C.white, fontWeight: 600 }}>{p.fitment}</span></>,
          { fontSize: 12.5, color: C.muted, lineHeight: 1.5, margin: '10px 0 0' }) : null}
        {p.mrp != null ? this.txt(<>₹{p.mrp} <span style={{ color: C.faint, fontWeight: 400, fontSize: 12 }}>MRP · incl. taxes</span></>, { fontSize: 19, fontWeight: 700, margin: '14px 0 0' }) : null}

        {this.metaRow(d)}

        {d.activatable ? (
          <div style={{ marginTop: 22 }}>
            <button className="rnrv-cta" onClick={this.activate} disabled={this.state.activating} style={{ ...this.btn(C.red, true), width: '100%', fontSize: 15, padding: '15px' }}>
              {this.state.activating ? (wd > 0 ? 'Activating…' : 'Registering…') : (this.state.activateErr ? `Retry — ${ctaLabel}` : ctaLabel)}
            </button>
            {this.state.activateErr
              ? this.txt('Something went wrong. Please check your connection and try again.', { fontSize: 12, color: C.danger, fontWeight: 600, margin: '10px 0 0' })
              : this.txt(wd > 0 ? `Starts your ${wd}-day warranty and registers this ${noun} as used.` : `Registers this ${noun} as used.`,
                { fontSize: 11.5, color: C.faint, margin: '10px 0 0' })}
          </div>
        ) : expired ? (
          <div style={{ marginTop: 18, background: `${C.amber}1f`, border: `1px solid ${C.amber}55`, borderRadius: 12, padding: '12px 14px' }}>
            {this.txt(<span style={{ color: C.amber, fontWeight: 700 }}>Warranty ended</span>, { fontSize: 13.5 })}
            {this.txt(`Registered & in use · expired ${d.warranty.expires}`, { fontSize: 11.5, color: C.muted, margin: '3px 0 0' })}
          </div>
        ) : d.warranty ? (
          <div style={{ marginTop: 18, background: `${C.green}1f`, border: `1px solid ${C.green}55`, borderRadius: 12, padding: '12px 14px' }}>
            {this.txt(<><span style={{ color: C.green, fontWeight: 700 }}>Warranty active</span> · <span style={{ color: C.white, fontWeight: 700 }}>{d.warranty.days_remaining} of {d.warranty.days_total} days left</span></>, { fontSize: 13.5 })}
            {this.txt(`Registered & in use · expires ${d.warranty.expires}`, { fontSize: 11.5, color: C.muted, margin: '3px 0 0' })}
          </div>
        ) : d.activated ? (
          <div style={{ marginTop: 18, background: `${C.green}1f`, border: `1px solid ${C.green}55`, borderRadius: 12, padding: '12px 14px' }}>
            {this.txt(<span style={{ color: C.green, fontWeight: 700 }}>Registered &amp; in use</span>, { fontSize: 13.5 })}
          </div>
        ) : null}

        {this.hasSeal(p)
          ? this.txt(<>Sealed &amp; serialized — one {noun}, one code. <span style={{ color: C.white, fontWeight: 700 }}>{noun === 'bottle' ? 'Insist on the seal; refuse refills.' : 'Insist on the seal.'}</span></>,
              { fontSize: 12, color: C.muted, lineHeight: 1.6, margin: '18px 0 0' })
          : this.txt(`One ${noun}, one code.`, { fontSize: 12, color: C.muted, lineHeight: 1.6, margin: '18px 0 0' })}

        {this.details(p)}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <Link className="rnrv-cta" to="/app" style={this.btn(C.red, true)}>Download the app</Link>
          <Link className="rnrv-cta" to="/booking" style={this.btn('transparent', false)}>Book a service</Link>
        </div>
        {this.footMeta(p)}
      </div>, C.green
    );
  }

  renderActivated(a, d) {
    const p = (d && d.product) || {};
    const w = a.warranty || {};
    const noun = this.noun(p);
    const registeredOnly = a.registered_only === true;
    const keepLine = registeredOnly ? 'Verified genuine by Ride N Repair.'
      : this.hasSeal(p) ? `Keep the ${noun} and seal until your warranty ends. Verified genuine by Ride N Repair.`
      : 'Keep the packaging until your warranty ends. Verified genuine by Ride N Repair.';
    return this.card(
      <div style={{ textAlign: 'center' }}>
        {this.badge(C.green, '✓', true)}
        {this.txt(registeredOnly ? (a.already ? 'ALREADY REGISTERED' : 'PRODUCT REGISTERED') : (a.already ? 'ALREADY ACTIVE' : 'WARRANTY ACTIVATED'),
          { fontSize: 12.5, letterSpacing: '.18em', color: C.green, fontWeight: 700 })}
        {this.txt(p.name || 'Your product', { fontSize: 23, fontWeight: 700, margin: '6px 0 2px' })}
        {this.txt('is registered & marked as used', { fontSize: 13.5, color: C.muted })}
        {registeredOnly ? null : (
          <div style={{ margin: '20px 0 0', background: `${C.green}14`, border: `1px solid ${C.green}44`, borderRadius: 14, padding: '18px' }}>
            {this.txt(`${w.days_total || this.warrantyDays(d) || 30} days`, { fontSize: 30, fontWeight: 700, color: C.green })}
            {this.txt('warranty', { fontSize: 12, color: C.muted, letterSpacing: '.1em', textTransform: 'uppercase' })}
            {w.expires ? this.txt(`Valid until ${w.expires}`, { fontSize: 12.5, color: C.white, margin: '8px 0 0' }) : null}
          </div>
        )}
        {this.metaRow(d)}
        {this.txt(keepLine, { fontSize: 12, color: C.muted, lineHeight: 1.6, margin: '16px 0 0' })}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <Link className="rnrv-cta" to="/app" style={this.btn(C.red, true)}>Download the app</Link>
          <Link className="rnrv-cta" to="/booking" style={this.btn('transparent', false)}>Book a service</Link>
        </div>
      </div>, C.green
    );
  }

  renderNotGenuine(d) {
    const flagged = d && d.verdict === 'flagged';
    const isVoid = d && d.verdict === 'void';
    const reportHref = 'https://wa.me/917302224253?text=I%20want%20to%20report%20a%20suspected%20counterfeit%20Ride%20N%20Repair%20product%20%E2%80%94%20code%20'
      + encodeURIComponent(this.getCode());
    return this.card(
      <div style={{ textAlign: 'center' }}>
        {this.badge(C.danger, '!')}
        {this.txt(isVoid ? 'CANCELLED' : flagged ? 'FLAGGED' : 'NOT VERIFIED', { fontSize: 12.5, letterSpacing: '.18em', color: C.danger, fontWeight: 700 })}
        {this.txt(isVoid ? 'This code was cancelled by Ride N Repair' : flagged ? 'This code was flagged' : 'We couldn’t verify this code', { fontSize: 22, fontWeight: 700, margin: '6px 0 10px' })}
        {this.txt(isVoid ? <><span style={{ color: C.white, fontWeight: 700 }}>Do not use this product.</span> If you were sold it as genuine, please report it to us.</>
          : ((d && d.message) || 'This code did not match a genuine Ride N Repair product.'), { fontSize: 14, color: C.muted, lineHeight: 1.6 })}
        <div style={{ background: `${C.danger}14`, border: `1px solid ${C.danger}44`, borderRadius: 12, padding: '12px 14px', margin: '16px 0 0' }}>
          {this.txt(<>Genuine Ride N Repair products are sealed and only verify on <span style={{ color: C.white, fontWeight: 700 }}>ridenrepair.com</span>. If the seal is broken or this looks suspicious, please report it.</>, { fontSize: 13, color: 'rgba(255,255,255,.86)', lineHeight: 1.6 })}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <a className="rnrv-cta" href={reportHref} target="_blank" rel="noopener noreferrer" style={this.btn(C.red, true)}>Report on WhatsApp</a>
          <Link className="rnrv-cta" to="/" style={this.btn('transparent', false)}>ridenrepair.com</Link>
        </div>
      </div>, C.danger
    );
  }

  renderError() {
    return this.card(
      <div style={{ textAlign: 'center' }}>
        {this.badge(C.amber, '…')}
        {this.txt('Verification unavailable', { fontSize: 20, fontWeight: 700, margin: '4px 0 8px' })}
        {this.txt('We couldn’t reach the verification service. Check your connection and try again.', { fontSize: 14, color: C.muted, lineHeight: 1.6 })}
        <button className="rnrv-cta" onClick={this.load} style={{ ...this.btn(C.red, true), width: '100%', marginTop: 18 }}>Retry</button>
      </div>, C.amber
    );
  }
}

export default QrVerifyPage;
